import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense, type ComponentType } from 'react'
import { FullScreenLoader } from '@/common/ui/FullScreenLoader'
import { Campaigns } from '@/campaigns/CampaignsPage'
import { Profile } from '@/user/profile/ProfilePage'
import { Portfolio } from '@/portfolio/PortfolioPage'
import Wallet from '@/wallet/WalletPage'
import { SecurityDetails } from '@/securities/SecurityDetailsPage.tsx'
import { APP_ROUTES } from '@/common/constants'
import RequirePrimaryAdviserContext from './RequirePrimaryAdviserContext'
import { KycInitPage as KycInit } from '@/onboarding/features/kyc-init/KycInitPage'
import { KycPage as Kyc } from '@/onboarding/KycPage'
import { Address } from '@/user/address/AddressPage'
import { MobileVerifyPage } from '@/onboarding/MobileVerifyPage'
import { Name } from '@/onboarding/NamePage'
import { MobileNumberPage } from '@/onboarding/MobileNumberPage'
import { Survey } from '@/user/survey/SurveyPage'
const OnboardingContainer = lazy(() =>
    import('@/onboarding/OnboardingContainer').then((m) => ({ default: m.OnboardingContainer })),
)
const ApplicationContainer = lazy(() =>
    import('@/application/ApplicationContainer').then((m) => ({ default: m.ApplicationContainer })),
)
const TermsAndConditionsUpdateContainer = lazy(() =>
    import('@/user/terms-and-conditions/TermsAndConditionsUpdateContainer').then((m) => ({
        default: m.TermsAndConditionsUpdateContainer,
    })),
)
import { CampaignDetailsPage } from '@/campaigns/CampaignDetailsPage'
import { ContactSupportPage } from '@/user/profile/contactSupportPage'
import { EsgProfile } from '@/research/EsgProfile'
import { ConfirmUser } from '@/auth/ConfirmUser'
import { InviteSignupPage } from '@/auth/InviteSignupPage'
import { RootContainer } from '../ui/RootContanier'
import { Dashboard } from '@/dashboard/Dashboard'
import { GettingStarted } from '@/dashboard/GettingStarted'
import ScrollToTop from '@/scrollToTop.tsx'
import { AboutYou } from '@/onboarding/AboutYouPage'
import { AccountTypePage } from '@/onboarding/AccountTypePage'
import { ApplicationResumePage } from '@/application/ResumePage'
import { ApplicationCreatePage } from '@/application/CreatePage'
import { ApplicationKycConfirmPage } from '@/application/KycConfirmPage'

let LazyApplicationAccountTypePage: ComponentType | undefined

if (import.meta.env.DEV || import.meta.env.VITE_ENV === 'dev') {
    LazyApplicationAccountTypePage = lazy(() =>
        import('@/application/AccountTypePage').then((m) => ({ default: m.ApplicationAccountTypePage })),
    )
}
import Rewards from '@/user/rewards/RewardsPage'
import { BundlePage } from '@/bundles/bundlePage'
import { Invest } from '@/invest/InvestPage'
import { FindStocks } from '@/findStocks/FindStocksPage'
import { AuthScreen } from '@/auth/AuthPage'
import { AuthTab } from '@/auth/types'
import { BlogsPage } from '@/blogs/BlogsPage'
import { BlogPage } from '@/blogs/BlogPage'
import ClientsPage from '@/adviser/ClientsPage'
import { AccountContainer } from '@/account/account-page/AccountContainer'
import AccountDetailsView from '@/account/account-page/AccountDetailsView'
import AccountSettingsView from '@/account/account-page/AccountSettingsView'
import AccountHoldersView from '@/account/account-page/AccountHoldersView'
import AccountTradeConfirmationView from '@/account/account-page/AccountTradeConfirmationView'
import AccountRegistrationAddressView from '@/account/account-page/AccountRegistrationAddressView'
import AccountTfnView from '@/account/account-page/AccountTfnView'
import { TradingAccountContainer } from '../ui/TradingAccountContainer'
import { MobileMorePage } from '../navigation/MobileMorePage'
import { TermsAndConditions } from '@/user/terms-and-conditions/termsAndConditions'
import { BankAccountPage } from '@/onboarding/BankAccountPage'
import { AccountConfirmation } from '@/onboarding/AccountConfirmationPage'
import { AccountCreated } from '@/onboarding/AccountCreatedPage'
import { SignedUpPage } from '@/onboarding/SignedUpPage'

export const router = createBrowserRouter([
    {
        path: APP_ROUTES.CONFIRM_USER,
        element: <ConfirmUser />,
    },
    {
        path: APP_ROUTES.SIGNUP_INVITE,
        element: <InviteSignupPage />,
    },
    {
        path: '/',
        element: (
            <ScrollToTop>
                <RootContainer />
            </ScrollToTop>
        ),
        children: [
            {
                path: APP_ROUTES.SIGNUP,
                element: <AuthScreen tab={AuthTab.SIGNUP} />,
            },
            {
                path: APP_ROUTES.CLIENTS,
                element: (
                    <RequirePrimaryAdviserContext>
                        <ClientsPage />
                    </RequirePrimaryAdviserContext>
                ),
            },
            {
                path: APP_ROUTES.APPLICATION_CREATE,
                element: <ApplicationCreatePage />,
            },
            {
                path: APP_ROUTES.APPLICATION_KYC_CONFIRM,
                element: <ApplicationKycConfirmPage />,
            },
            {
                path: APP_ROUTES.ACCOUNT,
                element: <AccountContainer />,
                children: [
                    {
                        index: true,
                        element: <AccountDetailsView />,
                    },
                    {
                        path: APP_ROUTES.ACCOUNT_DETAILS,
                        element: <AccountDetailsView />,
                    },
                    {
                        path: APP_ROUTES.ACCOUNT_SETTINGS,
                        element: <AccountSettingsView />,
                    },
                    {
                        path: APP_ROUTES.ACCOUNT_HOLDERS,
                        element: <AccountHoldersView />,
                    },
                    {
                        path: APP_ROUTES.ACCOUNT_TRADE_CONFIRMATION,
                        element: <AccountTradeConfirmationView />,
                    },
                    {
                        path: APP_ROUTES.ACCOUNT_REGISTRATION_ADDRESS,
                        element: <AccountRegistrationAddressView />,
                    },
                    {
                        path: APP_ROUTES.ACCOUNT_TFN,
                        element: <AccountTfnView />,
                    },
                ],
            },
            {
                element: <TradingAccountContainer />,
                children: [
                    {
                        path: APP_ROUTES.DASHBOARD,
                        element: <Dashboard />,
                    },
                    {
                        path: APP_ROUTES.CAMPAIGNS,
                        element: <Campaigns />,
                    },
                    {
                        path: APP_ROUTES.BLOGS,
                        element: <BlogsPage />,
                    },
                    {
                        path: `${APP_ROUTES.BLOGS}/:slug`,
                        element: <BlogPage />,
                    },
                    {
                        path: `${APP_ROUTES.CAMPAIGNS}/:campaignSlug`,
                        element: <CampaignDetailsPage />,
                    },
                    {
                        path: APP_ROUTES.PORTFOLIO,
                        element: <Portfolio />,
                    },
                    {
                        path: APP_ROUTES.TRADING,
                        element: <Navigate to={APP_ROUTES.INVEST} replace />,
                    },
                    {
                        path: APP_ROUTES.INVEST,
                        element: <Invest />,
                    },
                    {
                        path: APP_ROUTES.FIND_STOCKS,
                        element: <FindStocks />,
                    },
                    {
                        path: APP_ROUTES.WALLET,
                        element: <Wallet />,
                    },
                    {
                        path: APP_ROUTES.MORE,
                        element: <MobileMorePage />,
                    },
                    {
                        path: `${APP_ROUTES.SECURITY}/:securityCode`,
                        element: <SecurityDetails />,
                    },
                    {
                        path: `${APP_ROUTES.RESEARCH}/:securityCode`,
                        element: <EsgProfile />,
                    },
                    {
                        path: APP_ROUTES.PROFILE,
                        element: <Profile />,
                    },
                    {
                        path: APP_ROUTES.REWARDS,
                        element: <Rewards />,
                    },
                    {
                        path: APP_ROUTES.SUPPORT,
                        element: <ContactSupportPage />,
                    },
                    {
                        path: '/getting-started',
                        element: <GettingStarted />,
                    },
                    {
                        path: APP_ROUTES.APPLICATION_RESUME,
                        element: <ApplicationResumePage />,
                    },
                    {
                        path: `${APP_ROUTES.BUNDLES}/:bundleId`,
                        element: <BundlePage />,
                    },
                ],
            },
        ],
    },
    {
        path: '/onboarding',
        element: (
            <Suspense fallback={<FullScreenLoader dark />}>
                <OnboardingContainer />
            </Suspense>
        ),
        children: [
            {
                path: APP_ROUTES.ABOUT_YOU,
                element: <AboutYou />,
            },
            {
                path: APP_ROUTES.NAME,
                element: <Name />,
            },
            {
                path: APP_ROUTES.ADDRESS,
                element: <Address />,
            },
            {
                path: APP_ROUTES.MOBILE_NUMBER,
                element: <MobileNumberPage />,
            },
            {
                path: APP_ROUTES.MOBILE_VERIFICATION,
                element: <MobileVerifyPage />,
            },
            {
                path: APP_ROUTES.SIGNED_UP,
                element: <SignedUpPage />,
            },
            {
                path: APP_ROUTES.SURVEY,
                element: <Survey />,
            },
            {
                path: APP_ROUTES.ACCOUNT_TYPE,
                element: <AccountTypePage />,
            },
            {
                path: APP_ROUTES.TERMS_AND_CONDITIONS,
                element: <TermsAndConditions />,
            },
            {
                path: APP_ROUTES.KYC_INIT,
                element: <KycInit />,
            },
            {
                path: APP_ROUTES.KYC,
                element: <Kyc />,
            },
            {
                path: APP_ROUTES.BANK_ACCOUNT,
                element: <BankAccountPage />,
            },
            {
                path: APP_ROUTES.ACCOUNT_CONFIRMATION,
                element: <AccountConfirmation />,
            },
            {
                path: APP_ROUTES.ACCOUNT_CREATED,
                element: <AccountCreated />,
            },
        ],
    },
    {
        path: '/application',
        element: (
            <Suspense fallback={<FullScreenLoader dark />}>
                <OnboardingContainer />
            </Suspense>
        ),
        children: [
            {
                path: APP_ROUTES.APPLICATION_KYC,
                element: <Kyc />,
            },
        ],
    },
    {
        path: APP_ROUTES.TERMS_AND_CONDITIONS_UPDATE,
        element: (
            <Suspense fallback={<FullScreenLoader dark />}>
                <TermsAndConditionsUpdateContainer />
            </Suspense>
        ),
    },
    {
        path: '/application-test',
        element: (
            <Suspense fallback={<FullScreenLoader dark />}>
                <ApplicationContainer />
            </Suspense>
        ),
        children: [
            {
                path: 'account-type',
                element: LazyApplicationAccountTypePage ? (
                    <Suspense fallback={<FullScreenLoader dark />}>
                        <LazyApplicationAccountTypePage />
                    </Suspense>
                ) : null,
            },
        ],
    },
])
